
import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { sendPricingRequest, EmailTemplateParams } from "@/utils/emailService";

export interface LandingFormData {
  firstName: string;
  lastName: string;
  email: string;
  country: string;
}

export interface UseLandingFormProps {
  onAccessGranted: () => void;
}

// Session key no longer used - access is purely in-memory per app session

export const useLandingForm = ({ onAccessGranted }: UseLandingFormProps) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [formData, setFormData] = useState<LandingFormData>({
    firstName: "",
    lastName: "",
    email: "",
    country: ""
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value: string) => {
    setFormData(prev => ({ ...prev, country: value }));
  };

  // Function to fill in test credentials
  const fillTestCredentials = () => {
    setFormData({
      firstName: "Test",
      lastName: "User",
      email: "test@example.com",
      country: "United States"
    });
    setConsentChecked(true);
    toast.info("Test credentials applied. Click 'Access Product Catalog' to continue.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.country) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Check for test/fake data
    const testPatterns = [
      /^test$/i,
      /^test\s*user$/i,
      /^user$/i,
      /^sample$/i,
      /^example$/i,
      /^demo$/i,
      /^fake$/i,
      /^asdf/i,
      /^qwerty/i
    ];
    
    const fullName = `${formData.firstName} ${formData.lastName}`.toLowerCase();
    const isTestData = testPatterns.some(pattern => 
      pattern.test(formData.firstName.toLowerCase()) || 
      pattern.test(formData.lastName.toLowerCase()) ||
      pattern.test(fullName)
    );

    if (isTestData) {
      toast.error("Please enter your real name to access the product catalog");
      return;
    }

    // Check if consent is given
    if (!consentChecked) {
      toast.error("Please consent to receive communications from Aurora Multimedia Corp");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Check for test emails and disposable email services
    const blockedEmailPatterns = [
      'test@', '@test.', '@example.', 'fake@', '@fake.',
      '@temp.', '@tempmail.', '@guerrillamail.', '@mailinator.',
      '@10minutemail.', '@throwaway.', '@yopmail.', '@sharklasers.',
      '@trashmail.', 'noemail@', 'none@', 'na@'
    ];
    
    const emailLower = formData.email.toLowerCase();
    if (blockedEmailPatterns.some(pattern => emailLower.includes(pattern))) {
      toast.error("A valid email address is required to access the product catalog");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save access info to local storage
      const dataToStore = {
        ...formData,
        consent: consentChecked,
        timestamp: new Date().toISOString()
      };
      
      localStorage.setItem("appAccess", JSON.stringify(dataToStore));
      
      toast.success("Thank you! Redirecting to the product catalog...");
      
      // Send email in background (don't wait for it)
      const templateParams: EmailTemplateParams = {
        first_name: formData.firstName,
        last_Name: formData.lastName,
        email_address: formData.email,
        company_name: "N/A",
        job_title: "N/A",
        mobile_number: "N/A",
        Company_Address_Line_1: "N/A",
        Company_Address_Line_2: "N/A",
        Company_Address_Line_3: "N/A",
        Postal_Code: "N/A",
        interests: "Website Access",
        message: `${formData.firstName} ${formData.lastName} from ${formData.country} has accessed the website`,
        from_name: `${formData.firstName} ${formData.lastName}`,
        to_name: "Sales Team",
        country: formData.country,
        consent: "Yes",
        username: "N/A",
        password: "N/A"
      };

      sendPricingRequest(templateParams).catch(() => {});
      
      // Redirect to home page after brief delay
      setTimeout(() => {
        onAccessGranted();
      }, 1000);
      
    } catch (error: any) {
      const errorMessage = error.message || "There was a problem submitting your information";
      toast.error(errorMessage + ". Please try again or contact support.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    formData,
    consentChecked,
    isSubmitting,
    handleChange,
    handleCountryChange,
    setConsentChecked,
    handleSubmit,
    fillTestCredentials
  };
};
